/**
 * Storage Utilities
 * LocalStorage helpers for persisting user data and auth tokens
 */

import { User } from '../types';

// Storage keys
const STORAGE_KEYS = {
  AUTH_TOKEN: 'intellivision_auth_token',
  USER_DATA: 'intellivision_user_data',
  THEME: 'intellivision_theme',
} as const;

/**
 * Save data to localStorage
 * 
 * @param key - Storage key
 * @param value - Value to store (will be JSON stringified)
 */
export const saveToStorage = <T>(key: string, value: T): void => {
  try {
    const serialized = JSON.stringify(value);
    localStorage.setItem(key, serialized);
  } catch (error) {
    console.error(`Error saving to localStorage (${key}):`, error);
  }
};

/**
 * Get data from localStorage
 * 
 * @param key - Storage key
 * @returns Parsed value or null if not found
 */
export const getFromStorage = <T>(key: string): T | null => {
  try {
    const serialized = localStorage.getItem(key);
    if (serialized === null) return null;
    return JSON.parse(serialized) as T;
  } catch (error) {
    console.error(`Error reading from localStorage (${key}):`, error);
    return null;
  }
};

/**
 * Remove data from localStorage
 * 
 * @param key - Storage key
 */
export const removeFromStorage = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from localStorage (${key}):`, error);
  }
};

/**
 * Clear all localStorage data
 */
export const clearStorage = (): void => {
  try {
    localStorage.clear();
  } catch (error) {
    console.error('Error clearing localStorage:', error);
  }
};

// Auth Token Management

/**
 * Save auth token to localStorage
 * 
 * @param token - JWT token
 */
export const saveAuthToken = (token: string): void => {
  saveToStorage(STORAGE_KEYS.AUTH_TOKEN, token);
};

/**
 * Get auth token from localStorage
 * 
 * @returns JWT token or null
 */
export const getAuthToken = (): string | null => {
  return getFromStorage<string>(STORAGE_KEYS.AUTH_TOKEN);
};

/**
 * Remove auth token from localStorage
 */
export const removeAuthToken = (): void => {
  removeFromStorage(STORAGE_KEYS.AUTH_TOKEN);
};

// User Data Management

/**
 * Save user data to localStorage
 * 
 * @param user - User data object
 */
export const saveUserData = (user: User): void => {
  saveToStorage(STORAGE_KEYS.USER_DATA, user);
};

/**
 * Get user data from localStorage
 * 
 * @returns User data or null
 */
export const getUserData = (): User | null => {
  return getFromStorage<User>(STORAGE_KEYS.USER_DATA);
};

/**
 * Remove user data from localStorage
 */
export const removeUserData = (): void => {
  removeFromStorage(STORAGE_KEYS.USER_DATA);
};

// Theme Management

/**
 * Save theme preference to localStorage
 * 
 * @param theme - Theme name ('light' or 'dark')
 */
export const saveTheme = (theme: 'light' | 'dark'): void => {
  saveToStorage(STORAGE_KEYS.THEME, theme);
};

/**
 * Get theme preference from localStorage
 * 
 * @returns Theme name or null
 */
export const getTheme = (): 'light' | 'dark' | null => {
  return getFromStorage<'light' | 'dark'>(STORAGE_KEYS.THEME);
};

// Auth Helpers

/**
 * Check if user is authenticated
 * 
 * @returns true if auth token exists
 */
export const isAuthenticated = (): boolean => {
  const token = getAuthToken();
  return token !== null && token.length > 0;
};

/**
 * Clear all auth data (token + user data)
 */
export const clearAuthData = (): void => {
  removeAuthToken();
  removeUserData();
};

/**
 * Save complete auth session (token + user data)
 * 
 * @param token - JWT token
 * @param user - User data
 */
export const saveAuthSession = (token: string, user: User): void => {
  saveAuthToken(token);
  saveUserData(user);
};

/**
 * Get complete auth session
 * 
 * @returns Object with token and user data, or null if not authenticated
 */
export const getAuthSession = (): { token: string; user: User } | null => {
  const token = getAuthToken();
  const user = getUserData();

  if (!token || !user) return null;

  return { token, user };
};